import { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import * as uuid from 'uuid';

import ProviderCard from '../../../../ProviderCard';
import { ConfigurationContext } from '../../../../../context/configurationContext';
import { getProviderCards } from '../../../../../utils/provider';
import { ChatConversation } from '../../../../../common';
import { ConversationContext } from '../../../../../context/conversationContext';

import * as api from '../../../../../api';
import { Loader } from '@vivaahealth/design-system';

const PROVIDER_PAGE_SIZE = 3;

interface Props {
  message: ChatConversation;
}

const ProviderCards: React.FC<Props> = ({ message }) => {
  const configurationContext = useContext(ConfigurationContext);
  const conversationContext = useContext(ConversationContext);
  const [maxProvidersShown, setMaxProvidersShown] =
    useState(PROVIDER_PAGE_SIZE);
  const [isLoadingMoreProviders, setIsLoadingMoreProviders] = useState(false);

  const onAppointmentSelected = useCallback(
    (
      slot: api.EhrTimeSlot.EhrTimeSlot,
      provider: api.User.UserWithProfileAndPreference,
    ) => {
      if (!conversationContext) return;
      if (!configurationContext) return;
      conversationContext.disableMessages();
      if (!configurationContext.authenticated) {
        conversationContext.addNewMessage({
          chat_message_id: uuid.v4(),
          source_documents: [],
          response_entities: {
            recommendation: api.Assistant.Recommendation.Authentication,
          },
          data: { ehrTimeSlotToBook: slot, provider },
          user_entities: {},
          done: true,
          answer: 'To book your appointment please authenticate.',
          intent:
            'intent' in message
              ? message.intent
              : api.Assistant.Intent.GeneralInformation,
          intentComponent: undefined,
        });
      } else {
        conversationContext.addNewMessage({
          chat_message_id: uuid.v4(),
          source_documents: [],
          response_entities: {},
          user_entities: {},
          done: true,
          answer: 'Your appointment was booked.',
          intent: api.Assistant.Intent.GeneralInformation,
          intentComponent:
            api.Assistant.IntentComponent
              .GeneralInformationAppointmentConfirmed,
          data: {
            appointment: {
              providerFirstName: provider.firstName,
              providerLastName: provider.lastName,
              startTime: slot.startTime,
              providerPrefix: provider.prefix,
              providerSuffix: provider.suffix,
              previsitImageUrl: provider.previsitImageUrl,
              practiceName: provider.practices[0]?.name ?? 'PCMA',
              phoneNumber:
                provider.phoneNumber ??
                provider.practices[0]?.phoneNumber ??
                '3211234895',
              timezone:
                provider.practices[0]?.timezone ?? 'America/Los_Angeles',
              ehrTimeSlotBookingId: '1234',
              cancellationSuccess: false,
              address: provider.practices[0]?.address ?? '',
              state: provider.practices[0]?.state ?? '',
              city: provider.practices[0]?.city ?? '',
              zipCode: provider.practices[0]?.zip ?? '',
            },
          },
        });
      }
    },
    [conversationContext, configurationContext, message],
  );

  const handleLoadMore = useCallback(async () => {
    if (!('recommendedProviders' in message)) {
      setMaxProvidersShown(
        (prevMaxProvidersShown) => prevMaxProvidersShown + PROVIDER_PAGE_SIZE,
      );
      return;
    }
    if (!message.recommendedProviders?.length) return;
    if (message.recommendedProviders.length <= PROVIDER_PAGE_SIZE) return;
    if (!configurationContext?.id) return;
    setIsLoadingMoreProviders(true);
    try {
      const populatedProviders = await api.client.Assistant.getProviders(
        configurationContext.id,
        message.recommendedProviders
          .slice(maxProvidersShown, maxProvidersShown + PROVIDER_PAGE_SIZE)
          .map((provider) => provider.id),
      );
      conversationContext?.updateCurrentMessage({
        ...message,
        recommendedProviders: message.recommendedProviders.map((provider) => {
          const populatedProvider = populatedProviders.find(
            (populatedProvider) => populatedProvider.id === provider.id,
          );
          if (populatedProvider) return populatedProvider;
          return provider;
        }),
      });
      setMaxProvidersShown(
        (prevMaxProvidersShown) => prevMaxProvidersShown + PROVIDER_PAGE_SIZE,
      );
    } catch (err) {
      console.error('Error fetching providers', err);
    }
    setIsLoadingMoreProviders(false);
  }, [message, configurationContext, conversationContext, maxProvidersShown]);

  const { providers, total } = getProviderCards(message, maxProvidersShown);

  if (!providers?.length) return null;
  return (
    <ProviderWrapper>
      {providers.map((provider, i) => (
        <div key={`${provider.firstName}-${provider.lastName}`}>
          <ProviderCard
            provider={provider}
            showNotionalSlots={
              configurationContext?.previsitUseNotionalSchedulingTimeSlots
            }
            isOnlyOneCard={providers.length === 1}
            disabled={message.disabled}
            onAppointmentSelected={(slot) =>
              onAppointmentSelected(slot, provider)
            }
          />
        </div>
      ))}
      {isLoadingMoreProviders && <Loader label="Loading more providers..." />}
      {total > maxProvidersShown && !isLoadingMoreProviders ? (
        <ShowMoreButton
          type="button"
          disabled={message.disabled}
          onClick={() => handleLoadMore()}
        >
          + See more providers
        </ShowMoreButton>
      ) : null}
    </ProviderWrapper>
  );
};

const ShowMoreButton = styled.button`
  border: none;
  background: transparent;
  color: var(--action-color);
  font-size: 14px;
  font-weight: 600;

  @media (max-width: 680px) {
    min-width: 240px;
    border: 1px solid var(--action-color);
  }

  &:disabled {
    color: var(--disabled-color);
    cursor: not-allowed;
  }
`;

const ProviderWrapper = styled.div`
  > div {
    margin-bottom: 16px;
  }

  > div:last-child {
    margin-bottom: 64px;
  }

  @media (max-width: 680px) {
    display: flex;
    overflow-x: auto;
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    margin-top: 12px;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 24px;

    > div {
      padding-right: 12px;
      display: flex;
      margin-bottom: 0;
    }

    > div:last-child {
      padding-right: 0;
      margin-bottom: 0;
    }
  }
`;

export default ProviderCards;
